import axios, { AxiosError, AxiosInstance } from "axios";
import config from 'Config';
import { Result } from "Models/result";
import sessionService from 'Store/module/sessionService'
import { currentLocale } from 'Lang/index';

class Repository {
    constructor() {
        this._baseUrl = baseUrlFactory.getProvider();
        this._instance = this.createAxios();
    }
    private _baseUrl: IBaseUrlProvider;
    private _instance: AxiosInstance;

    getHeader = function () {
        const header: any = {
            'cache-control': 'no-cache',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': 'Content-Type',
            'Access-Control-Allow-Methods': 'GET,POST,OPTIONS,DELETE,PUT',
            'Content-Type': 'application/json',
            'mode': 'no-cors'
        };
        return header;
    };

    createAxios = () => {
        const axiosAuth = axios.create({
            baseURL: this._baseUrl.getUrl(),
            headers: this.getHeader(),
            validateStatus: function (status) {
                return status < 500; // Resolve only if the status code is less than 500
            }
        });

        axiosAuth.interceptors.request.use(
            function (req) {
                const currentLanguage = currentLocale();
                const session = sessionService.get();
                if (!!session && req.headers) {
                    req.headers['Authorization'] = `Bearer ${session.token}`;
                }
                if (req.data) {
                    if (typeof req.data == 'object') {
                        req.data["languageId"] = currentLanguage;
                    }
                }

                if (req.params) {
                    req.params['languageId'] = currentLanguage;
                }
                return req;
            },
            (err) => {
                return Promise.reject(err);
            }
        );

        axiosAuth.interceptors.response.use((response) => {
            return response;
        }, (error) => {
            if (axios.isAxiosError(error)) {
                handleAxiosError(error);
            } else {
                handleUnexpectedError(error);
            }
            if (error.response) {
                return Promise.reject(error.response);
            }
            return Promise.reject(error.message);
        });

        return axiosAuth;
    }

    get = async (url: string): Promise<any> => {
        try {
            return await this._instance.get(url);
        } catch (error) {
            return {
                data: null,
                message: error
            };
        }
    }

    post = async (url: string, body: any = null): Promise<any> => {
        try {
            return await this._instance.post(url, body);
        } catch (error) {
            return {
                data: null,
                message: error
            };
        }
    }

    postForm = async (url: string, body: any): Promise<any> => {
        try {
            return await this._instance.post(url, body, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
        } catch (error) {
            return {
                data: null,
                message: error
            };
        }
    }

    downloadFile = async (url: string, body?: any, fileType?: string): Promise<Result<any>> => {
        const res = await this.getBolbUrl(url, body, fileType);
        if (res.isSuccess) {
            let fileName = Date.now().toString();
            if (fileType) {
                fileName += `.${fileType.split('/')[1]}`;
            }
            else {
                fileName += '.pdf';
            }
            const link = document.createElement('a');
            link.href = res.data as string;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            return new Result<any>({
                isSuccess: true,
                message: "",
                data: null
            });
        }

        return new Result<any>({
            data: null,
            message: res.message,
            isSuccess: false
        });
    }

    getBolbUrl = async (url: string, body?: any, fileType?: string): Promise<Result<string>> => {
        try {
            var response = await this._instance.post(url, body, {
                responseType: 'blob'
            });
            if (!fileType) {
                fileType = 'application/pdf';
            }
            const bjectURL = this.createObjectURL(response.data, fileType);
            return new Result<any>({
                isSuccess: true,
                message: "",
                data: bjectURL
            });
        } catch (error) {
            return new Result<any>({
                data: null,
                message: error as string,
                isSuccess: false
            });
        }
    }


    private isArray(myArray: object): boolean {
        return myArray.constructor.toString().indexOf("Array") > -1;
    }

    private getErrorMessage(data: any): string {
        try {
            if (data && !data.message && data.code != 200 && data.errors) {
                let message = '';
                if (typeof data.errors == 'object') {
                    if (this.isArray(data.errors)) {
                        for (let i = 0; i < data.errors.length; i++) {
                            message += data.errors[i];
                        }
                    } else {
                        for (let i in data.errors) {
                            const item = data.errors[i];
                            if (this.isArray(item)) {
                                for (let i = 0; i < item.length; i++) {
                                    message += item[i];
                                }
                            } else {
                                for (let i in item) {
                                    message += i;
                                }
                            }
                        }
                    }
                    return message;
                }
                return data.errors;
            }
            return data.message;
        } catch {
            return "Something went wrong. Please try again later";
        }
    }

    getResult<TData>(res: any): Result<TData> {
        if (res) {
            if(res.status == 401)
            {
                window.location.href='/m/login';
                return new Result<TData>();
            }
            const data = res.data;
            const result = new Result<TData>({
                data: data?.data,
                isSuccess: data?.code == 200,
                message: this.getErrorMessage(data),
                statusCode: res.status
            });

            return result;
        }
        return new Result<TData>({
            isSuccess: false,
            message: "Something went wrong. Please try again later"
        })
    }

    base64ToArrayBuffer = function (base64: string): Uint8Array {
        var binaryString = window.atob(base64);
        var binaryLen = binaryString.length;
        var bytes = new Uint8Array(binaryLen);
        for (var i = 0; i < binaryLen; i++) {
            var ascii = binaryString.charCodeAt(i);
            bytes[i] = ascii;
        }
        return bytes;
    }

    getBlobUrlFromBase64(base64: string, fileType?: string) {
        const arr = this.base64ToArrayBuffer(base64);
        return this.createObjectURL(arr, fileType);
    }

    createObjectURL(arr: Uint8Array, fileType?: string): string {
        if (!fileType) {
            fileType = 'application/pdf';
        }
        return window.URL.createObjectURL(new Blob([arr], { type: fileType }));
    }

    downloadFileExecute(blobUrl: string, fileName?: string) {
        if (!fileName) {
            fileName = Date.now().toString() + ".pdf";
        }
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
    }

    private static _instance: Repository;
    static instance() {
        if (this._instance == null) {
            this._instance = new Repository();
        }
        return this._instance;
    }
}


function handleAxiosError(error: AxiosError<any, any>) {
    //console.error(error);
    if (error.response?.status == 401) {
        alert("You are not authorized");
    }

    window.location.href='/m/login';
}

function handleUnexpectedError(error: any) {
    console.error(error);
}

interface IBaseUrlProvider {
    getUrl(): string;
}

class baseUrlProvider implements IBaseUrlProvider {
    getUrl(): string {
        return config.BASE_API_URL as string;
    }
}

class baseUrlFromUrlProvider implements IBaseUrlProvider {
    getUrl(): string {
        return window.location.origin;
    }
}

export class baseUrlFactory {
    static getProvider(): IBaseUrlProvider {
        if (window.location.origin.indexOf('localhost:808') != -1 || window.location.origin.indexOf('192.168.1.8:808') != -1) {
            return new baseUrlProvider();
        }
        return new baseUrlFromUrlProvider();
    }
}

export default Repository.instance();
